// Building readiness's three areas on its status row. Mirrors
// inrange_agents/building_readiness_record.py; see
// docs/Y2026-Q3/design-building-readiness-status.md.

export type AreaKey = "structural" | "roof_condition" | "on_site_survey";

export const AREA_ORDER: readonly AreaKey[] = [
  "structural",
  "roof_condition",
  "on_site_survey",
];

export const AREA_LABEL: Record<AreaKey, string> = {
  structural: "Structural",
  roof_condition: "Roof condition",
  on_site_survey: "On-site survey",
};

/** An action's or date's area by name; "" for one outside the three. */
export const areaLabel = (area: string): string =>
  (AREA_ORDER as readonly string[]).includes(area)
    ? AREA_LABEL[area as AreaKey]
    : "";

export interface ProgressStep {
  label: string;
  state: "done" | "now" | "todo" | "gap";
}

export interface ReadinessDecision {
  outcome: string;
  state: "" | "pending" | "likely" | "taken";
  by: string;
  date: string;
  note: string;
  upload_id: string;
  // Absent on entries written before sources carried their quote.
  quote?: string;
}

export interface ReadinessPlayer {
  name: string;
  people: string;
  role: string;
}

export interface ReadinessConstraint {
  requirement: string;
  set_by: string;
  adherence: "meets" | "deviates" | "not_evidenced" | "conflict" | "open";
  label: string;
  accepts_deviation: string;
  evidence: string;
  upload_id: string;
  quote?: string;
}

export interface TimelineEvent {
  date: string;
  until: string;
  what: string;
  source: string;
  upload_id: string;
  quote?: string;
}

export interface ReadinessStep {
  text: string;
  owner: string;
  on_us: boolean;
  target_date: string;
  date_basis: "fixed" | "predicted";
  // Why it is due then; shown on hover over the date.
  date_why: string;
  status: string;
  later: boolean;
  attention: boolean;
  context: string;
  raised: string;
  action: "" | "email" | "chase" | "agent";
  upload_id: string;
  quote?: string;
}

export interface ReadinessEvidence {
  what: string;
  detail: string;
  origin: "" | "commissioned" | "bma_form" | "out_of_band" | "received";
  standing: "current" | "held" | "superseded" | "not_received" | "email_only" | "conflict";
  label: string;
  upload_id: string;
  quote?: string;
}

export interface ReadinessObservation {
  raised: string;
  text: string;
  sits_with: string;
  state: "open" | "resolved" | "info";
  label: string;
}

export interface ReadinessArea {
  stage: string;
  summary: string;
  status: string;
  progress: ProgressStep[];
  decision: ReadinessDecision;
  remediation_items: string[];
  players: ReadinessPlayer[];
  constraints: ReadinessConstraint[];
  timeline: TimelineEvent[];
  next_steps: ReadinessStep[];
  evidence: ReadinessEvidence[];
  observations: ReadinessObservation[];
}

export interface BuildingReadiness {
  areas: Partial<Record<AreaKey, ReadinessArea>>;
  next_steps: ReadinessStep[];
}
